import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { useIdioma } from '../../contexts/IdiomaContext';
import { misPaginasService } from '../../services/api';
import { membershipService } from '../../services/api';
import styled, { keyframes } from 'styled-components';
import NeuralOrbIcon from '../NeuralOrbIcon';
import { buildBg } from '../landing/bgHelpers';
import CustomMenu from '../landing/CustomMenu';
import useEntregasPendientes, { esTutor, textoMenuEntregas } from '../entregas/useEntregasPendientes';
import PaisSelector from '../landing/PaisSelector';
import { resolveDeviceValue } from '../admin/DeviceTabsSelect';
import HeaderSection from '../landing/sections/HeaderSection';
import { seccionVisibleParaSesion } from '../landing/SectionRenderer';

// Animaciones
const orbitSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const coreGlow = keyframes`
  0%, 100% {
    filter: drop-shadow(0 0 4px rgba(20, 182, 203, 0.4));
    transform: scale(1);
  }
  50% {
    filter: drop-shadow(0 0 12px rgba(20, 182, 203, 0.9));
    transform: scale(1.05);
  }
`;

const HeaderContainer = styled.header`
  width: 100%;
  background: ${p => p.$bg || '#ffffff'};
  color: #0f355b;
  padding: ${p => p.$padding || '0.8rem 15px'};
  box-shadow: ${p => p.$shadow || '0 1px 20px rgba(15, 53, 91, 0.08)'};
  ${p => p.$noBorder
    ? 'border-bottom: none;'
    : `border-bottom: 3px solid transparent;
  border-image: linear-gradient(90deg, #0f355b 0%, #14b6cb 50%, #0f355b 100%) 1;`}
  position: ${p => p.$fixed ? 'fixed' : 'relative'};
  left: 0;
  right: 0;
  ${p => p.$fixed ? (p.$posBottom ? 'bottom: 0;' : 'top: 0;') : ''}
  z-index: 900;
  transition: background 0.3s ease, box-shadow 0.3s ease;

  @media (max-width: 768px) {
    padding: ${p => p.$mobilePadding || '0.8rem 1rem'};
    /* El fijo con fondo sólido pasa a relativo en móvil para no tapar el
       contenido. El transparente (overlay) flota SOBRE el banner igual que en
       escritorio → conserva position: fixed también en móvil/tablet. */
    ${p => (p.$fixed && !p.$transparent) ? 'position: relative; top: auto; bottom: auto;' : ''}
  }
`;

/* Reserva el alto del header cuando es fijo (solo desktop), para que el
   contenido no quede oculto debajo. En móvil el header es relativo → sin spacer. */
const HeaderSpacer = styled.div`
  height: ${p => p.$h || 0}px;
  @media (max-width: 768px) { display: none; }
`;

const ContainerHeader = styled.div`
  max-width: ${p => p.$maxWidth || '1200px'};
  width: ${p => p.$ancho || 'auto'};
  margin: 0 auto;
  display: flex;
  justify-content: ${p => p.$center ? 'center' : 'space-between'};
  align-items: center;
  padding: ${p => p.$padding || '0'};
  ${p => p.$menuLeft ? '& > *:not(:first-child) { order: -1; }' : ''}
  ${p => p.$hideMenu ? '& > *:not(:first-child) { display: none; }' : ''}
  /* Barra flotante (misma opción que la sección header del builder): el color
     vive en esta píldora y el contenedor exterior queda transparente. */
  ${p => p.$barra && `
    background: ${p.$barraBg || '#fff'};
    border-radius: ${p.$barraRadio || '0 0 40px 40px'};
    box-shadow: ${p.$barraSombra || '0 4px 18px rgba(15,23,42,0.10)'};
  `}

  @media (max-width: 768px) {
    padding: ${p => p.$mobilePadding || '0'};
    ${p => p.$barra && p.$anchoMobile ? `width: ${p.$anchoMobile};` : ''}
    ${p => p.$barra && `border-radius: ${p.$barraRadioMovil || '0 0 22px 22px'};`}
  }
`;

const Logo = styled(Link)`
  font-size: 1.6rem;
  font-weight: 700;
  color: ${p => p.$color || '#0f355b'};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  transition: all 0.3s ease;
  font-family: 'Myriad Pro', sans-serif;
  letter-spacing: -0.5px;

  &:hover {
    color: ${p => p.$hover || '#14b6cb'};
  }
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${coreGlow} 3s ease-in-out infinite;

  .orbit-ring {
    animation: ${orbitSpin} 8s linear infinite;
    transform-origin: 50% 50%;
  }
`;

const ButtonMenu = styled.button`
  background: none;
  border: 1px solid #e2e8f0;
  color: #0f355b;
  font-size: 1.3rem;
  cursor: pointer;
  display: none;
  padding: 0.4rem 0.6rem;
  border-radius: 6px;
  transition: all 0.3s ease;

  &:hover {
    background: #f1f5f9;
    border-color: #14b6cb;
  }

  @media (max-width: 768px) {
    display: block;
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-family: 'Myriad Pro', sans-serif;

  @media (max-width: 768px) {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    flex-direction: column;
    gap: 0;
    padding: 0.5rem 1rem 1rem;
    display: ${props => (props.open ? 'flex' : 'none')};
    box-shadow: 0 8px 24px rgba(15, 53, 91, 0.12);
    border-top: 1px solid #e2e8f0;
    z-index: 1000;
  }
`;

const NavLink = styled(Link)`
  color: #475569;
  text-decoration: none;
  font-weight: 600;
  padding: 0.4rem 1rem;
  border-radius: 6px;
  transition: all 0.2s ease;
  font-size: 0.9rem;

  &:hover {
    color: #0f355b;
    background: #f1f5f9;
  }

  @media (max-width: 768px) {
    width: 100%;
    text-align: center;
    padding: 0.75rem 1rem;
  }
`;

const Button = styled.button`
  background: ${p => p.$grad || 'linear-gradient(135deg, #0f355b 0%, #1a4a7a 100%)'};
  border: none;
  color: white;
  padding: 0.45rem 1.25rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Myriad Pro', sans-serif;

  &:hover {
    background: ${p => p.$gradHover || 'linear-gradient(135deg, #14b6cb 0%, #0e9aad 100%)'};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(20, 182, 203, 0.3);
  }

  @media (max-width: 768px) {
    width: 100%;
    text-align: center;
    margin-top: 0.5rem;
  }
`;

const AvatarLink = styled(Link)`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: ${p => p.$grad || 'linear-gradient(135deg, #14b6cb, #0f355b)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.78rem;
  font-weight: 700;
  color: white;
  text-decoration: none;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 2px 10px rgba(20, 182, 203, 0.4);
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    margin: 0.5rem auto 0;
  }
`;

const LangSelector = styled.div`
  display: flex;
  gap: 2px;
  background: #f1f5f9;
  border-radius: 6px;
  padding: 2px;
  margin-left: 0.5rem;

  @media (max-width: 768px) {
    justify-content: center;
    margin: 0.75rem 0 0;
    padding: 4px;
    gap: 4px;
    width: 100%;
    box-sizing: border-box;
    border-top: 1px solid #e2e8f0;
    padding-top: 0.85rem;
    border-radius: 0;
    background: transparent;
    position: relative;
    /* Label "Idioma" arriba del selector solo en mobile */
    &::before {
      content: 'Idioma';
      position: absolute;
      top: 0;
      left: 50%;
      transform: translate(-50%, -50%);
      background: white;
      padding: 0 0.6rem;
      font-size: 0.7rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 1.5px;
      color: #94a3b8;
    }
  }
`;

const LangPill = styled.button`
  padding: 0.25rem 0.5rem;
  border: none;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.$active ? (props.$activeBg || '#0f355b') : 'transparent'};
  color: ${props => props.$active ? '#fff' : '#64748b'};

  &:hover {
    background: ${props => props.$active ? (props.$activeBg || '#0f355b') : '#e2e8f0'};
  }

  @media (max-width: 768px) {
    flex: 1;
    padding: 0.55rem 0.7rem;
    font-size: 0.85rem;
    border-radius: 8px;
    background: ${props => props.$active ? (props.$activeBg || '#0f355b') : '#f1f5f9'};
  }
`;

const BACKEND_BASE = '/backend';

const ProjectLogoImg = styled.img`
  height: ${p => p.$h || '40px'};
  width: ${p => p.$w || 'auto'};
  max-width: ${p => p.$w ? 'none' : '140px'};
  object-fit: contain;
  ${p => p.$wTablet ? `@media (max-width: 991px) and (min-width: 601px) { width: ${p.$wTablet}; max-width: none; }` : ''}
  ${p => p.$wMobile ? `@media (max-width: 600px) { width: ${p.$wMobile}; max-width: none; }` : ''}
`;

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const { proyecto } = useProject();
  const ocultarPerfil = !!proyecto?.config_json?.menu_config?.ocultar_perfil;
  // Tutor de entregas: «Entregas por revisar (n)» en el menú.
  const { pendientes: entregasPendientes } = useEntregasPendientes(esTutor(user) ? user : null);
  const textoEntregas = textoMenuEntregas(proyecto, entregasPendientes);
  const { idioma, setIdioma, t } = useIdioma();
  const [showWaLink, setShowWaLink] = useState(false);
  const [menuPaginas, setMenuPaginas] = useState([]);

  // Páginas personalizables visibles en el menú (filtradas por país/rol/idioma/login)
  useEffect(() => {
    if (!proyecto?.id) { setMenuPaginas([]); return; }
    misPaginasService.menu(proyecto.id, idioma)
      .then(res => setMenuPaginas(res.data?.paginas || []))
      .catch(() => setMenuPaginas([]));
  }, [proyecto?.id, idioma, user]);

  // Check membership for WA Training link visibility
  useEffect(() => {
    if (!user) { setShowWaLink(false); return; }
    if (user.role === 'admin') { setShowWaLink(true); return; }

    membershipService.getMisProyectos()
      .then(res => {
        const memberships = res.data?.memberships || [];
        // Coordinadores ven el link siempre (necesitan conectar WA)
        // Otros roles solo si WA ya está conectado
        const hasWa = memberships.some(m =>
          m.rol_proyecto === 'coordinador' || m.whatsapp_connected
        );
        setShowWaLink(hasWa);
      })
      .catch(() => setShowWaLink(false));
  }, [user]);

  const handleLogout = () => {
    logout();
    // Redirección post-logout configurable por proyecto (misma lógica que HeaderSection).
    const rd = proyecto?.config_json?.auth_config?.logout_destino || proyecto?.config_json?.logout_redirect_url;
    if (rd && /^https?:\/\//i.test(rd)) window.location.href = rd;
    else if (rd && rd.startsWith('/')) navigate(rd);   // p. ej. «/» en INS: la portada, sin el modal de acceso
    else navigate('/login');
  };

  // Header de marca por proyecto: hereda los mismos estilos que el header del
  // builder de la landing (config_json → sección tipo 'header'), para que el
  // header de las páginas internas coincida con el de la landing de cada proyecto.
  const hdrCfg = useMemo(() => {
    const cj = proyecto?.config_json;
    if (!cj) return null;
    const pick = (arr) => Array.isArray(arr)
      ? arr.find(s => s?.tipo === 'header' && s?.visible !== false)
      : null;
    const sec = pick(cj.secciones_globales) || pick(cj.landing_secciones);
    return sec?.config || null;
  }, [proyecto]);

  const primary = hdrCfg?.color_primario || proyecto?.color_primario || '#0f355b';
  const secondary = hdrCfg?.color_secundario || proyecto?.color_secundario || '#14b6cb';
  const hasBrand = !!(hdrCfg?.color_primario || hdrCfg?.color_secundario
    || proyecto?.color_primario || proyecto?.color_secundario);

  // Estilos de contenedor: solo se aplican si el proyecto tiene header configurado
  // en el builder (si no, se conserva el estilo por defecto actual, sin regresión).
  // Igual que HeaderSection: el padding va en el contenedor interno (no el externo).
  const containerBg = hdrCfg ? buildBg(hdrCfg, '#ffffff') : undefined;
  const containerShadow = hdrCfg ? (hdrCfg.sombra || '0 2px 8px rgba(0,0,0,0.06)') : undefined;
  const outerPadding = hdrCfg ? '0' : undefined;
  const outerMobilePadding = hdrCfg ? '0' : undefined;
  const innerPadding = hdrCfg ? (hdrCfg.padding || '0.75rem 2rem') : undefined;
  const innerMobilePadding = hdrCfg ? '0.5rem 1rem' : undefined;
  const logoHeight = hdrCfg ? (hdrCfg.altura_logo || '48px') : '40px';
  // Ancho del logo por dispositivo (opcional). Vacío = comportamiento actual (width auto).
  const logoW = resolveDeviceValue(hdrCfg?.ancho_logo, '');
  // Mostrar/ocultar el menú y su posición respecto al logo (solo si hay header del builder).
  const mostrarMenu = hdrCfg ? (hdrCfg.mostrar_menu !== false) : true;
  const menuLeft = hdrCfg?.posicion_menu === 'izquierda';

  // Posición fija (igual que HeaderSection: c.fijo + posicion_fija arriba/abajo).
  const fixed = !!(hdrCfg && hdrCfg.fijo);
  const posBottom = hdrCfg?.posicion_fija === 'abajo';
  // Si el header es transparente, flota SOBRE el contenido (como en la landing):
  // la sección sube por detrás y no se reserva espacio. Solo reservamos hueco
  // cuando el header fijo tiene fondo sólido (que sí taparía el contenido).
  // Barra flotante: misma opción que la sección header del builder, para que las
  // páginas internas conserven la píldora del diseño de la landing.
  const barraFlotante = hdrCfg?.barra_flotante === true;
  const barraAncho = resolveDeviceValue(hdrCfg?.barra_ancho, '');
  const isTransparent = !!(hdrCfg && hdrCfg.fondo_transparente) || barraFlotante;
  const reserveSpace = fixed && !isTransparent;

  // Fondo que aparece al hacer scroll (hereda config del header del builder).
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    if (!fixed || !hdrCfg?.activar_scroll_color) { setIsScrolled(false); return; }
    const onScroll = () => setIsScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [fixed, hdrCfg]);
  const scrollActivo = isScrolled && hdrCfg?.activar_scroll_color;

  // Menú personalizado del proyecto (config_json.menu_config). Si tiene ítems,
  // reemplaza la navegación por defecto del header (con su propia hamburguesa ≤991px).
  const menuCfg = proyecto?.config_json?.menu_config;
  const menuItems = Array.isArray(menuCfg?.items) ? menuCfg.items : [];

  // Color del texto de marca (logo/nombre) — campo color_texto del header.
  const textColor = hdrCfg?.color_texto || (hasBrand ? primary : undefined);

  // Logo propio del header (c.logo) con fallback al logo del proyecto, igual que HeaderSection.
  const headerLogoUrl = hdrCfg?.logo
    ? (hdrCfg.logo.startsWith('http') ? hdrCfg.logo : `${BACKEND_BASE}/${hdrCfg.logo}`)
    : (proyecto?.logo ? `${BACKEND_BASE}/${proyecto.logo}` : null);

  // Colores de marca para botones/avatar/idiomas (solo si el proyecto los define)
  const btnGrad = hasBrand ? `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)` : undefined;
  const btnGradHover = hasBrand ? `linear-gradient(135deg, ${secondary} 0%, ${primary} 100%)` : undefined;
  const avatarGrad = hasBrand ? `linear-gradient(135deg, ${secondary}, ${primary})` : undefined;
  const brandHover = hasBrand ? secondary : undefined;
  const langActiveBg = hasBrand ? primary : undefined;

  // Cuando el header es fijo, reservamos su alto (solo desktop) para no tapar contenido.
  const headerRef = useRef(null);
  const [spacerH, setSpacerH] = useState(0);
  useLayoutEffect(() => {
    if (!reserveSpace) { setSpacerH(0); return; }
    const el = headerRef.current;
    if (!el) return;
    const update = () => setSpacerH(el.offsetHeight || 0);
    update();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
    }
    window.addEventListener('resize', update);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', update); };
  }, [reserveSpace, hdrCfg, menuOpen]);

  // ── Header unificado en páginas internas ─────────────────────────────────
  // En vez de IMITAR el header de la landing, renderizamos el MISMO componente
  // HeaderSection con la sección global del proyecto. Así el header de las
  // páginas internas (p. ej. /contenidos) queda idéntico en diseño y estructura
  // al de la home. Si el proyecto no tiene header global configurado, se usa el
  // header por defecto de más abajo (sin regresión para otros proyectos).
  const globalHeaderSec = useMemo(() => {
    const cj = proyecto?.config_json;
    if (!cj) return null;
    const pick = (arr) => Array.isArray(arr)
      ? arr.find(s => s?.tipo === 'header' && s?.visible !== false)
      : null;
    return pick(cj.secciones_globales) || pick(cj.landing_secciones) || null;
  }, [proyecto]);

  // tl: resuelve campos traducibles (string | {es,en,pt}) igual que DynamicLanding.
  const tlGlobal = useMemo(() => (value, fallback = '') => {
    if (value == null) return fallback;
    if (typeof value === 'string') return value || fallback;
    if (typeof value === 'object') return value[idioma] || value['es'] || fallback;
    return fallback;
  }, [idioma]);

  // El header de páginas internas usa la MISMA configuración que la landing
  // (incluida su transparencia): así queda idéntico en diseño y estructura.
  const gsCfg = globalHeaderSec?.config;

  // Solo reservamos espacio si el header es FIJO y con fondo sólido (el
  // transparente flota sobre el contenido, igual que en la landing).
  const gsReserve = !!(gsCfg?.fijo) && !gsCfg?.fondo_transparente;
  const gsWrapRef = useRef(null);
  const [gsSpacerH, setGsSpacerH] = useState(0);
  useLayoutEffect(() => {
    if (!gsReserve) { setGsSpacerH(0); return; }
    const measure = () => {
      const el = gsWrapRef.current?.querySelector('header');
      setGsSpacerH(el?.offsetHeight || 0);
    };
    measure();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      const el = gsWrapRef.current?.querySelector('header');
      if (el) { ro = new ResizeObserver(measure); ro.observe(el); }
    }
    window.addEventListener('resize', measure);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [gsReserve, globalHeaderSec]);

  if (globalHeaderSec) {
    // El header global puede restringirse por sesión desde el admin
    // (config.mostrar_si). Si no aplica, la página va sin header — no se cae al
    // header por defecto, porque el admin pidió explícitamente ocultarlo.
    if (!seccionVisibleParaSesion(globalHeaderSec.config, user)) return null;
    return (
      <>
        <div ref={gsWrapRef}>
          <HeaderSection
            config={globalHeaderSec.config}
            proyecto={proyecto}
            tl={tlGlobal}
            openLogin={() => navigate('/login')}
            openRegister={() => navigate('/register')}
          />
        </div>
        {gsReserve && <HeaderSpacer $h={gsSpacerH} />}
      </>
    );
  }

  return (
    <>
    <HeaderContainer
      ref={headerRef}
      $bg={barraFlotante && !scrollActivo ? 'transparent' : (scrollActivo ? (hdrCfg.color_fondo_scroll || '#ffffff') : containerBg)}
      $shadow={barraFlotante ? 'none' : (scrollActivo ? (hdrCfg.sombra_scroll || containerShadow) : containerShadow)}
      $padding={outerPadding}
      $mobilePadding={outerMobilePadding}
      $noBorder={!!hdrCfg}
      $fixed={fixed}
      $transparent={isTransparent}
      $posBottom={posBottom}
    >
      <ContainerHeader
        $padding={innerPadding}
        $mobilePadding={innerMobilePadding}
        $hideMenu={!mostrarMenu}
        $menuLeft={menuLeft}
        $barra={barraFlotante}
        $barraBg={hdrCfg?.barra_fondo}
        $barraRadio={hdrCfg?.barra_radio}
        $barraRadioMovil={hdrCfg?.barra_radio_movil}
        $barraSombra={hdrCfg?.barra_sombra}
        $ancho={barraAncho.desktop || undefined}
        $anchoMobile={barraAncho.mobile || undefined}
        $maxWidth={hdrCfg?.ancho_maximo}
        $center={hdrCfg?.centrar_contenido === true}
      >
        <Logo to="/" $color={textColor} $hover={brandHover}>
          {headerLogoUrl ? (
            <ProjectLogoImg src={headerLogoUrl} alt={proyecto?.nombre || 'Logo'} $h={logoHeight} $w={logoW.desktop || undefined} $wTablet={logoW.tablet || undefined} $wMobile={logoW.mobile || undefined} />
          ) : (
            <>
              <NeuralOrbIcon size={40} />
              <span>{proyecto?.nombre || 'MentorIA'}</span>
            </>
          )}
        </Logo>
        {menuItems.length > 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <CustomMenu
              items={menuItems}
              posicion={menuCfg.posicion || 'derecha'}
              fontSize={menuCfg.font_size}
              movilBg={menuCfg.movil_fondo}
              movilAncho={menuCfg.movil_ancho}
              movilFontSize={menuCfg.movil_font_size}
              movilColor={menuCfg.movil_color_texto}
              subBg={menuCfg.submenu_fondo}
              subColor={menuCfg.submenu_color}
              hoverColor={hdrCfg?.color_menu_hover || menuCfg?.color_hover || undefined}
              hoverBg={hdrCfg?.fondo_menu_hover || undefined}
              colorOverride={hdrCfg?.color_menu || undefined}
              itemColors={hdrCfg?.menu_colores || undefined}
              bgMenu={hdrCfg?.fondo_menu || undefined}
              radiusMenu={hdrCfg?.radio_menu || undefined}
              padMenu={hdrCfg?.padding_menu || undefined}
              activoBg={menuCfg.fondo_activo || undefined}
              activoColor={menuCfg.color_activo || undefined}
              user={user}
              accent={textColor || '#0f355b'}
              onLogin={() => navigate('/login')}
              onRegister={() => navigate('/login?registro=1')}
              onLogout={handleLogout}
            />
            {user && !ocultarPerfil && (
              <AvatarLink to="/perfil" title="Mi perfil" $grad={avatarGrad}>
                {user.avatar_url
                  ? <img src={`/backend/${user.avatar_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                  : (user.nombre || user.email || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
              </AvatarLink>
            )}
            {(() => {
              const disp = proyecto?.config_json?.idiomas_disponibles
                ?? proyecto?.config_json?.idiomas_habilitados;
              const langs = (Array.isArray(disp) ? disp : []).map(l => l.toLowerCase());
              if (langs.length <= 1) return null;
              return (
                <LangSelector>
                  {langs.map(lang => (
                    <LangPill key={lang} $active={idioma === lang} $activeBg={langActiveBg} onClick={() => setIdioma(lang)}>
                      {lang.toUpperCase()}
                    </LangPill>
                  ))}
                </LangSelector>
              );
            })()}
            {hdrCfg?.selector_paises === true && (hdrCfg?.selector_paises_logueado === true || !user) && (
              <PaisSelector
                colorBorde={hdrCfg.selector_paises_borde}
                colorBordeAncho={hdrCfg.selector_paises_borde_ancho}
                colorBordeHover={hdrCfg.selector_paises_borde_hover}
                colorTextoHover={hdrCfg.selector_paises_color_hover}
                colorTexto={hdrCfg.selector_paises_color}
                colorFondo={hdrCfg.selector_paises_fondo}
                acento={hdrCfg.selector_paises_acento || secondary}
                tamano={hdrCfg.selector_paises_tamano}
                mostrarReset={hdrCfg.selector_paises_reset !== false}
              />
            )}
          </div>
        ) : (
        <>
        <ButtonMenu onClick={() => setMenuOpen(!menuOpen)}>
          ☰
        </ButtonMenu>

        <Nav open={menuOpen} onClick={() => setMenuOpen(false)}>
          {user?.role === 'admin' && (
            <NavLink to="/admin-panel" onClick={() => setMenuOpen(false)}>
              {t('panel_admin')}
            </NavLink>
          )}
          {user ? (
            <>
              <NavLink to="/contenidos" onClick={() => setMenuOpen(false)}>{t('contenidos')}</NavLink>
              {showWaLink && (
                <NavLink to="/wa-training" onClick={() => setMenuOpen(false)}>{t('entrenamiento_wa')}</NavLink>
              )}
              {!!Number(user.es_tutor) && (
                <NavLink to="/entregas" onClick={() => setMenuOpen(false)}>{textoEntregas}</NavLink>
              )}
              <Button $grad={btnGrad} $gradHover={btnGradHover} onClick={() => { handleLogout(); setMenuOpen(false); }}>{t('cerrar_sesion')}</Button>
              {!ocultarPerfil && (
                <AvatarLink to="/perfil" onClick={() => setMenuOpen(false)} title="Mi perfil" $grad={avatarGrad}>
                  {user.avatar_url
                    ? <img src={`/backend/${user.avatar_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                    : (user.nombre || user.email || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
                  }
                </AvatarLink>
              )}
            </>
          ) : (
            <>
              <NavLink to="/login" onClick={() => setMenuOpen(false)}>{t('iniciar_sesion')}</NavLink>
              <NavLink to="/register" onClick={() => setMenuOpen(false)}>{t('registrarse')}</NavLink>
            </>
          )}
          {menuPaginas.map(pg => (
            <NavLink key={pg.id} to={`/p/${pg.slug}`} onClick={() => setMenuOpen(false)}>
              {pg.titulo || pg.slug}
            </NavLink>
          ))}
          <LangSelector>
            {(() => {
              // Mostrar solo los idiomas habilitados del proyecto (si los define);
              // si no hay config, mostrar los tres por compatibilidad.
              const disp = proyecto?.config_json?.idiomas_disponibles
                ?? proyecto?.config_json?.idiomas_habilitados;
              const langs = (Array.isArray(disp) ? disp : []).map(l => l.toLowerCase());
              if (langs.length <= 1) return null; // 0 o 1 idioma → no mostrar selector
              return langs.map(lang => (
                <LangPill
                  key={lang}
                  $active={idioma === lang}
                  $activeBg={langActiveBg}
                  onClick={() => { setIdioma(lang); setMenuOpen(false); }}
                >
                  {lang.toUpperCase()}
                </LangPill>
              ));
            })()}
          </LangSelector>
        </Nav>
        </>
        )}
      </ContainerHeader>
    </HeaderContainer>
    {reserveSpace && <HeaderSpacer $h={spacerH} />}
    </>
  );
};

export default Header;
